import { useState } from 'react';
import { X } from 'lucide-react';
import type { MembershipPlan } from '../api/extractionApi';
import { Button } from '@/shared/ui';
import styles from './PracticeProfileStep.module.css';
import localStyles from './MembershipPlansEditor.module.css';

export function MembershipPlansEditor({
  plans,
  onChange,
  onConfirm,
}: {
  plans: MembershipPlan[];
  onChange: (plans: MembershipPlan[]) => void;
  onConfirm: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<MembershipPlan>({
    name: '',
    cost: '',
    benefits: [],
    moneySaved: '',
  });
  const [benefitInput, setBenefitInput] = useState('');

  const resetDraft = () => {
    setDraft({ name: '', cost: '', benefits: [], moneySaved: '' });
    setBenefitInput('');
  };

  const addBenefit = () => {
    if (!benefitInput.trim()) return;
    setDraft((d) => ({ ...d, benefits: [...d.benefits, benefitInput.trim()] }));
    setBenefitInput('');
  };

  const removeBenefit = (idx: number) => {
    setDraft((d) => ({ ...d, benefits: d.benefits.filter((_, i) => i !== idx) }));
  };

  const savePlan = () => {
    if (!draft.name.trim()) return;
    const plan = {
      ...draft,
      name: draft.name.trim(),
      cost: draft.cost.trim(),
      moneySaved: draft.moneySaved.trim(),
    };
    if (editingIdx !== null) {
      const updated = [...plans];
      updated[editingIdx] = plan;
      onChange(updated);
      setEditingIdx(null);
    } else {
      onChange([...plans, plan]);
    }
    resetDraft();
    setAdding(false);
  };

  const startEdit = (idx: number) => {
    setDraft({ ...plans[idx] });
    setEditingIdx(idx);
    setAdding(true);
  };

  const removePlan = (idx: number) => {
    onChange(plans.filter((_, i) => i !== idx));
  };

  return (
    <div className={styles.editorCol}>
      {/* Existing plans */}
      {plans.map((plan, idx) => (
        <div key={idx} className={styles.planCard}>
          <div className={styles.planRow}>
            <div className={localStyles.flex1}>
              <div className={styles.planName}>{plan.name}</div>
              {plan.cost && (
                <div className={`${styles.planCost} ${localStyles.accentText}`}>{plan.cost}</div>
              )}
              {plan.moneySaved && (
                <div className={`${styles.planSaved} ${localStyles.mutedText}`}>
                  Save {plan.moneySaved}
                </div>
              )}
              {plan.benefits.length > 0 && (
                <div className={styles.benefitTags}>
                  {plan.benefits.map((b, i) => (
                    <span
                      key={i}
                      className={`${styles.benefitTag} ${localStyles.benefitTagAccent}`}
                    >
                      {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.planActions}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const copy = {
                    ...plan,
                    benefits: [...plan.benefits],
                    name: `${plan.name} - Copy`,
                  };
                  onChange([...plans, copy]);
                }}
              >
                Copy
              </Button>
              <Button size="sm" variant="secondary" onClick={() => startEdit(idx)}>
                Edit
              </Button>
              <Button size="sm" variant="destructive" onClick={() => removePlan(idx)}>
                <X size={12} aria-hidden />
              </Button>
            </div>
          </div>
        </div>
      ))}

      {/* Add / Edit form */}
      {adding ? (
        <div className={`${styles.planCard} ${localStyles.planCardActive}`}>
          <div className={styles.planFormTitle}>
            {editingIdx !== null ? 'Edit Plan' : 'New Plan'}
          </div>
          <div className={styles.editorCol}>
            <div>
              <label className={`${styles.fieldLabel} ${localStyles.mutedText}`}>Plan Name *</label>
              <input
                type="text"
                placeholder="e.g. Adult Savings Plan"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className={localStyles.formInput}
              />
            </div>
            <div className={styles.planCostRow}>
              <div className={localStyles.flex1}>
                <label className={`${styles.fieldLabel} ${localStyles.mutedText}`}>Cost</label>
                <input
                  type="text"
                  placeholder="e.g. $299/year"
                  value={draft.cost}
                  onChange={(e) => setDraft((d) => ({ ...d, cost: e.target.value }))}
                  className={localStyles.formInput}
                />
              </div>
              <div className={localStyles.flex1}>
                <label className={`${styles.fieldLabel} ${localStyles.mutedText}`}>
                  Money Saved
                </label>
                <input
                  type="text"
                  placeholder="e.g. $500+"
                  value={draft.moneySaved}
                  onChange={(e) => setDraft((d) => ({ ...d, moneySaved: e.target.value }))}
                  className={localStyles.formInput}
                />
              </div>
            </div>
            <div>
              <label className={`${styles.fieldLabel} ${localStyles.mutedText}`}>Benefits</label>
              {draft.benefits.length > 0 && (
                <div className={styles.benefitTags}>
                  {draft.benefits.map((b, i) => (
                    <span
                      key={i}
                      className={`${styles.benefitTagRemovable} ${localStyles.benefitTagAccent}`}
                    >
                      {b}
                      <span onClick={() => removeBenefit(i)} className={styles.removeX}>
                        <X size={10} aria-hidden />
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <div className={styles.benefitInputRow}>
                <input
                  type="text"
                  placeholder="e.g. 2 cleanings per year"
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addBenefit();
                    }
                  }}
                  className={`${localStyles.formInput} ${localStyles.flex1}`}
                />
                <Button size="sm" variant="secondary" onClick={addBenefit}>
                  + Add
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.planFormActions}>
            <Button size="sm" variant="primary" onClick={savePlan}>
              {editingIdx !== null ? 'Update Plan' : 'Add Plan'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setAdding(false);
                setEditingIdx(null);
                resetDraft();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className={localStyles.addPlanBtn}
          onClick={() => {
            resetDraft();
            setAdding(true);
          }}
        >
          + Add Plan
        </Button>
      )}

      {/* Save & Confirm */}
      <div className={styles.confirmRow}>
        <Button size="sm" variant="primary" onClick={onConfirm}>
          Save &amp; Confirm
        </Button>
      </div>
    </div>
  );
}
